import { useMemo } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { useOnboarding } from "./OnboardingProvider";

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourTooltipProps {
  targetRect: SpotlightRect | null;
  targetEl: Element | null;
}

const TOOLTIP_GAP = 12;
const TOOLTIP_WIDTH = 320;

export function TourTooltip({ targetRect }: TourTooltipProps) {
  const { t } = useTranslation("common");
  const {
    activeTour,
    currentStepIndex,
    nextStep,
    prevStep,
    skipTour,
  } = useOnboarding();

  const currentStep = activeTour?.steps[currentStepIndex] ?? null;
  const totalSteps = activeTour?.steps.length ?? 0;
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === totalSteps - 1;

  // Resolve i18n keys with fallback
  const title = useMemo(() => {
    if (currentStep?.titleKey) {
      const translated = t(currentStep.titleKey, { defaultValue: "" });
      if (translated && translated !== currentStep.titleKey) return translated;
    }
    return currentStep?.title ?? "";
  }, [currentStep, t]);

  const description = useMemo(() => {
    if (currentStep?.descriptionKey) {
      const translated = t(currentStep.descriptionKey, { defaultValue: "" });
      if (translated && translated !== currentStep.descriptionKey) return translated;
    }
    return currentStep?.description ?? "";
  }, [currentStep, t]);

  // i18n button labels with hardcoded English fallback
  const skipLabel = t("onboarding.tour.skip", { defaultValue: "Skip" });
  const backLabel = t("onboarding.tour.back", { defaultValue: "Back" });
  const nextLabel = t("onboarding.tour.next", { defaultValue: "Next" });
  const doneLabel = t("onboarding.tour.done", { defaultValue: "Done" });

  // i18n step counter
  const stepOfLabel = t("onboarding.tour.stepOf", {
    current: currentStepIndex + 1,
    total: totalSteps,
    defaultValue: `Step ${currentStepIndex + 1} of ${totalSteps}`,
  });

  // Calculate tooltip position
  const position = useMemo(() => {
    if (!targetRect || !currentStep) {
      return {
        top: window.innerHeight / 2 - 80,
        left: window.innerWidth / 2 - TOOLTIP_WIDTH / 2,
      };
    }

    const placement = currentStep.placement;
    let top = 0;
    let left = 0;

    switch (placement) {
      case "bottom":
        top = targetRect.top + targetRect.height + TOOLTIP_GAP;
        left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case "top":
        top = targetRect.top - TOOLTIP_GAP - 160;
        left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case "right":
        top = targetRect.top + targetRect.height / 2 - 80;
        left = targetRect.left + targetRect.width + TOOLTIP_GAP;
        break;
      case "left":
        top = targetRect.top + targetRect.height / 2 - 80;
        left = targetRect.left - TOOLTIP_WIDTH - TOOLTIP_GAP;
        break;
    }

    left = Math.max(12, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - 12));
    top = Math.max(12, top);

    return { top, left };
  }, [targetRect, currentStep]);

  if (!currentStep || !activeTour) return null;

  const actionHint =
    currentStep.action === "click"
      ? t("onboarding.tour.clickHint", {
          defaultValue: "\uD83D\uDC46 Click the highlighted element to continue",
        })
      : currentStep.action === "navigate"
        ? t("onboarding.tour.navigateHint", {
            defaultValue: "Navigate to continue",
          })
        : null;

  const showNextButton = currentStep.action !== "click";
  const showEstimatedTime = isFirst && activeTour.estimatedMinutes != null && activeTour.estimatedMinutes > 0;

  return (
    <motion.div
      key={currentStep.id}
      className="fixed z-[70] pointer-events-auto w-80 rounded-xl border bg-white p-5 shadow-xl dark:bg-slate-900 dark:border-slate-700"
      style={{ top: position.top, left: position.left }}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Step indicator dots + counter */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span
              key={i}
              className={`block size-1.5 rounded-full transition-all duration-300 ${
                i === currentStepIndex
                  ? "w-4 bg-primary"
                  : i < currentStepIndex
                    ? "bg-primary/40"
                    : "bg-slate-200 dark:bg-slate-700"
              }`}
            />
          ))}
        </div>
        <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
          {stepOfLabel}
        </span>
      </div>

      {/* Content */}
      <h3 className="mb-1.5 text-base font-semibold text-slate-900 dark:text-slate-100">
        {title}
      </h3>
      <p className="mb-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {description}
      </p>

      {showEstimatedTime && (
        <p className="mb-2 text-xs text-slate-400 dark:text-slate-500">
          {t("onboarding.tour.estimatedTime", {
            minutes: activeTour.estimatedMinutes,
            defaultValue: `~${activeTour.estimatedMinutes} min`,
          })}
        </p>
      )}

      {actionHint && (
        <p className="mb-3 text-xs italic text-blue-500 dark:text-blue-400">
          {actionHint}
        </p>
      )}

      {/* Action buttons */}
      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          onClick={skipTour}
          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-400 transition-all duration-150 hover:bg-slate-100 hover:text-slate-600 active:bg-slate-200 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300 dark:active:bg-slate-700"
        >
          {skipLabel}
        </button>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              type="button"
              onClick={prevStep}
              className="flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-all duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800 active:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-100"
            >
              <ArrowLeft className="size-3" />
              {backLabel}
            </button>
          )}
          {showNextButton && (
            <button
              type="button"
              onClick={nextStep}
              className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-semibold transition-all duration-150 ${
                isLast
                  ? "bg-green-600 text-white hover:bg-green-700 active:bg-green-800 shadow-sm hover:shadow"
                  : "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 shadow-sm hover:shadow"
              }`}
            >
              {isLast ? (
                <>
                  <Check className="size-3.5" strokeWidth={3} />
                  {doneLabel}
                </>
              ) : (
                <>
                  {nextLabel}
                  <ArrowRight className="size-3" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
